import * as React from "react";
import { useRef, useState } from "react";
import { ComposerAttach } from "../../../types";
import { useDismiss } from "../../../hooks/useDismiss";
import { Plus, Paperclip, Shield, ImageGlyph } from "../../icons";
import styles from "./AttachMenu.module.css";

/** Default enterprise-security reassurance copy, overridable via `attach.securityNote` / `attach.securityLink`. */
const DEFAULT_SECURITY_NOTE = "All files attached are safe with our enterprise grade security policy.";
const DEFAULT_SECURITY_LINK = {
	label: "Learn More",
	href: "https://www.parrotanalytics.com/insights/the-enterprise-ai-infrastructure-behind-parrot-analytics-decision-intelligence/",
};

interface Props {
	attach: ComposerAttach;
	/** Opens the composer's file picker, which hands the chosen files to `attach.onUploadFiles`. */
	onPickFiles: () => void;
}

export const AttachMenu: React.FC<Props> = ({ attach, onPickFiles }) => {
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	useDismiss(open, rootRef, () => setOpen(false));

	const pick = (fn?: () => void) => {
		setOpen(false);
		fn?.();
	};

	// `securityNote === null` hides the note entirely; `undefined` falls back to the default copy.
	const note = attach.securityNote === undefined ? DEFAULT_SECURITY_NOTE : attach.securityNote;
	const link = attach.securityLink ?? (attach.securityNote === undefined ? DEFAULT_SECURITY_LINK : undefined);

	return (
		<div className={styles.root} ref={rootRef}>
			<button
				type="button"
				className={styles.trigger}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label="Add attachment"
				onClick={() => setOpen((o) => !o)}
			>
				<Plus />
			</button>
			{open && (
				<div className={styles.menu} role="menu">
					{attach.onUploadFiles && (
						<button
							type="button"
							role="menuitem"
							className={styles.item}
							onClick={() => pick(onPickFiles)}
						>
							<Paperclip />
							<span>Upload Files</span>
						</button>
					)}
					{attach.onUploadImages && (
						<button
							type="button"
							role="menuitem"
							className={styles.item}
							onClick={() => pick(attach.onUploadImages)}
						>
							<ImageGlyph />
							<span>Upload Images</span>
						</button>
					)}
					{note && (
						<div className={styles.disclaimer}>
							<Shield />
							{note}
							{link && (
								<>
									{" "}
									<a href={link.href} target="_blank" rel="noopener noreferrer">
										{link.label}
									</a>
								</>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};
